import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { clearTimelineType, loadTimelineTypeDetails } from '../../../store/directusService';
import { compareUnorderedArrays } from '../../../utils/arrayUtils';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_UPDATE_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { initialErrorState, validate } from './validateTimelineAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const TimelineAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const viewRoles = [LOCAL_ADMIN, SUPERUSER];
    const acceptedRoles = [SUPERUSER];
    const collectionName = 'timeline_type';
    const urlName = 'timeline_type';
    const [keys, setKeys] = useState({ timelineTypeId: '0' });
    const [errors, setErrors] = useState(initialErrorState);
    const [selectedTimelineTypes, setSelectedTimelineTypes] = useState([]);
    const [preSelectedTimelineTypeIds, setPreSelectedTimelineTypeIds] = useState([]);
    const [preSelectedTimelineTypes, setPreSelectedTimelineTypes] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const timelineTypeDetails = useSelector(
        (state) => state.entities.directusService.timelineTypeDetails
    );
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);

    // USE EFFECTS
    useEffect(() => {
        if (!contractId) return;
        contractSetup();
    }, [contractId]);

    useEffect(() => {
        if (timelineTypeDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                collectionName,
                timelineTypeDetails,
                contractId
            );
            setPreSelectedTimelineTypes(preSelects);
            setPreSelectedTimelineTypeIds(preSelectIds);
        }
    }, [timelineTypeDetails]);

    useEffect(() => {
        if (successMessage.includes('Updating timeline_type collection')) contractSetup();
    }, [successMessage]);

    // HELPER FNS
    const contractSetup = () => {
        setIsDisabled(false);
        setSelectedTimelineTypes([]);
        setPreSelectedTimelineTypes([]);
        setErrors(initialErrorState);
        setKeys(clearKeys(keys));
        dispatch(clearTimelineType());
        dispatch(loadTimelineTypeDetails());
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const loadingError = () => {
        if (!contractId) return 'No contract';
        if (timelineTypeDetails?.length < 1) return 'No timeline types';
    };

    // EVENT HANDLERS
    const onTimelineTypeChange = (chosenIds) => {
        setIsDisabled(false);
        clearError('timelineTypeId');
        if (!chosenIds) chosenIds = [];
        setSelectedTimelineTypes(chosenIds);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const changesToTimelineType = !compareUnorderedArrays(
            preSelectedTimelineTypeIds,
            selectedTimelineTypes
        );
        if (!changesToTimelineType) {
            setErrors({ button: { error: true, message: NO_UPDATE_ERROR } });
            return;
        }

        const newErrors = validate(selectedTimelineTypes);

        if (Object.keys(newErrors)?.length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsDisabled(true);

        changesToTimelineType &&
            updateSelectedItems(
                urlName,
                timelineTypeDetails,
                selectedTimelineTypes,
                contractId,
                collectionName,
                dispatch
            );
    };

    // AWAITING CONTENT
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <>
            <form className={form.formWrapper} onSubmit={onSubmit} data-testid="form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="timelineTypeId"
                            key={keys.timelineTypeId}
                            label="Timeline Type"
                            placeholder="Select timeline type..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={timelineTypeDetails || []}
                            preSelectedIds={preSelectedTimelineTypeIds}
                            preSelects={preSelectedTimelineTypes}
                            error={errors.timelineTypeId}
                            onChange={(chosenIds) => onTimelineTypeChange(chosenIds)}
                        />
                    </div>
                </div>
                <Button
                    id="timelineTypeAdmin"
                    content="UPDATE TIMELINE TYPE"
                    disabled={!hasRole(acceptedRoles, roles) || isDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </>
    );
};

TimelineAdmin.propTypes = {
    contractId: PropTypes.string
};

export default TimelineAdmin;
